import React, { ChangeEvent, ElementType, useCallback, useMemo } from "react";
import { LimeInputProps, LimeInput } from "./LimeInput";
import { MantineFormValues } from "./shared.types";
import getUnicodeFlagIcon from "country-flag-icons/unicode";
import PhoneInput, {
  DefaultInputComponentProps,
} from "react-phone-number-input";
import {
  InputProps,
  Select,
  SelectItem,
  SelectProps,
  Skeleton,
  Spinner,
} from "@heroui/react";
import { cn } from "@/utils";
import { resolveCountrySelection } from "./limePhoneInput.utils";
import { t } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";

export type LimePhoneInputProps = MantineFormValues &
  Pick<DefaultInputComponentProps, "defaultCountry"> & {
    label?: string;
    onChange: (value: string) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    isDisabled?: boolean;
    disablePointerEvents?: boolean;
    variant?: InputProps["variant"];
    autoComplete?: DefaultInputComponentProps["autoComplete"];
    placeholder?: string;
    isLoading?: boolean;
    isSkeleton?: boolean;
    className?: string;
  } & {
    // Support for HTML data-* attributes
    [key: `data-${string}`]: string | undefined;
  };

export const LimePhoneInput = React.memo(
  React.forwardRef<HTMLInputElement, LimePhoneInputProps>(
    ({ ...props }, ref) => {
      const countrySelectComponent = useCallback(
        (p: CustomCountrySelectProps) => (
          <CustomCountrySelectWithIcon {...p} variant={props.variant} />
        ),
        [props.variant],
      );

      const error = useMemo(() => {
        if (!props.error) return undefined;

        if (typeof props.error === "string") {
          return (
            <p className="text-danger-500 text-xs" role="alert">
              {props.error}
            </p>
          );
        }

        return props.error;
      }, [props.error]);

      return (
        <div className="flex flex-col gap-1">
          <Skeleton
            isLoaded={!props.isSkeleton}
            className={cn(props.className, "w-full rounded-xl")}
          >
            <PhoneInput
              {...props}
              disabled={props.isDisabled}
              className={cn("group h-14", {
                "pointer-events-none": props.disablePointerEvents,
              })}
              inputComponent={CustomInput}
              countrySelectComponent={countrySelectComponent}
              inputRef={ref}
            />
          </Skeleton>

          {error}
        </div>
      );
    },
  ),
);

LimePhoneInput.displayName = "LimePhoneInput";

interface CustomInputProps extends LimeInputProps {
  variant?: InputProps["variant"];
  error?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const CustomInput = React.memo(
  React.forwardRef<HTMLInputElement, CustomInputProps>((props, ref) => (
    <LimeInput
      ref={ref}
      {...props}
      autoComplete="tel"
      variant={props.variant ?? "bordered"}
      classNames={{
        mainWrapper: "h-14",
        inputWrapper: "h-14",
        base: "h-14",
      }}
      error={props.error}
      errorMessage={undefined}
      endContent={props.isLoading ? <Spinner size="sm" /> : props.endContent}
      onKeyDown={props.onKeyDown}
    />
  )),
);
CustomInput.displayName = "CustomInput";

const CustomCountrySelectWithIcon = React.memo(
  ({
    value,
    options,
    iconComponent: Icon,
    arrowComponent,
    unicodeFlags,
    ...rest
  }: CustomCountrySelectWithIconProps) => {
    const selectedOption = useMemo(() => {
      return getSelectedOption(options, value);
    }, [options, value]);

    return (
      <div className="PhoneInputCountry">
        <CustomCountrySelect
          {...rest}
          value={value}
          options={options}
          className={"PhoneInputCountrySelect"}
          disabled={rest.disabled}
        />

        {/* Either a Unicode flag icon. */}
        {unicodeFlags && value && (
          <div className="PhoneInputCountryIconUnicode">
            {getUnicodeFlagIcon(value)}
          </div>
        )}

        {/* Or an SVG flag icon. */}
        {!(unicodeFlags && value) && Icon && (
          <div
            className={cn(
              "rounded-medium border-default-200 group-[.PhoneInput--focus]:border-default-foreground flex h-14 w-14 items-center justify-center border-2",
              {
                "bg-default-100 border-0": rest.variant === "flat",
                "opacity-disabled": rest.disabled,
              },
            )}
          >
            <Icon
              aria-hidden
              country={value}
              label={selectedOption?.label}
              aspectRatio={unicodeFlags ? 1 : undefined}
            />
          </div>
        )}
      </div>
    );
  },
);

CustomCountrySelectWithIcon.displayName = "CustomCountrySelectWithIcon";

type CustomCountrySelectWithIconProps = CustomCountrySelectProps & {
  iconComponent?: ElementType;
  arrowComponent?: ElementType;
  unicodeFlags?: boolean;
};

const CustomCountrySelect = React.memo(
  ({
    name,
    value,
    onChange,
    onFocus,
    onBlur,
    options,
    disabled,
    readOnly,
    tabIndex,
    className,
    variant,
  }: CustomCountrySelectProps) => {
    const onChange_ = useCallback(
      (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        const result = resolveCountrySelection(value, options);
        if (result.type === "ignore") {
          Sentry.captureMessage(
            `LimePhoneInput: ignored unknown country code "${value}"`,
            "warning",
          );
          return;
        }
        onChange(result.country);
      },
      [onChange, options],
    );

    const selectedKeys = useMemo(() => {
      return value ? [value] : ["ZZ"];
    }, [value]);

    const selectItems = useMemo(() => {
      return options.map(({ value, label, divider }) => (
        <SelectItem
          key={divider ? "|" : value || "ZZ"}
          textValue={divider ? "|" : value || "ZZ"}
          startContent={getUnicodeFlagIcon(value || "ZZ")}
        >
          {label}
        </SelectItem>
      ));
    }, [options]);

    return (
      <Select
        label={name}
        selectedKeys={selectedKeys}
        variant={variant ?? "bordered"}
        onChange={onChange_}
        onFocus={onFocus}
        onBlur={onBlur}
        name={name}
        disabled={disabled}
        aria-readonly={readOnly}
        tabIndex={tabIndex ? Number(tabIndex) : undefined}
        className={cn(className, "data-[focus=true]:border-default-foreground")}
        isDisabled={disabled}
        classNames={{
          popoverContent: "w-fit",
          listboxWrapper: "min-w-52 w-fit",
        }}
        selectionMode="single"
        disallowEmptySelection
        aria-label={t`Izberi državo`}
        popoverProps={{
          shouldCloseOnScroll: false,
          shouldBlockScroll: false,
        }}
      >
        {selectItems}
      </Select>
    );
  },
);

CustomCountrySelect.displayName = "CustomCountrySelect";

type CustomCountrySelectProps = {
  name?: string;
  value?: string; // Two letter country code (e.g. "US", "RU")
  onChange: (value?: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  options: {
    value?: string;
    label: string;
    divider?: boolean;
  }[];
  disabled?: boolean;
  readOnly?: boolean;
  tabIndex?: number | string;
  className?: string;
  variant?: SelectProps["variant"];
};

function getSelectedOption(
  options: {
    value?: string;
    label: string;
    divider?: boolean;
  }[],
  value?: string,
) {
  for (const option of options) {
    if (!option.divider && option.value === value) {
      return option;
    }
  }
}
